'use client';

import { useState } from 'react';

interface CodeBlockProps {
  children: React.ReactNode;
  className?: string;
  filename?: string;
}

export default function CodeBlock({ children, className, filename }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const language = className?.replace('language-', '') || 'text';
  const code = String(children).trim();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  return (
    <div className="relative group my-6">
      {/* Language label and copy button */}
      <div className="flex items-center justify-between px-4 py-2 bg-surface/50 border border-border-muted border-b-0 rounded-t-lg">
        <div className="flex items-center gap-2">
          {filename && (
            <span className="text-xs text-foreground-tertiary">{filename}</span>
          )}
          {!filename && (
            <span className="text-xs text-accent uppercase tracking-wider font-mono">
              {language}
            </span>
          )}
        </div>
        <button
          onClick={handleCopy}
          className="px-3 py-1 text-xs bg-accent-muted hover:bg-accent-muted text-accent rounded border border-border hover:border-accent/40 transition-colors flex items-center gap-2"
          aria-label="Copy code"
        >
          {copied ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>

      {/* Code content */}
      <pre className="overflow-x-auto p-4 bg-surface/80 border border-border-muted rounded-b-lg">
        <code className={className}>{code}</code>
      </pre>
    </div>
  );
}
